import { t } from 'i18next';
import { useState, useMemo, useEffect } from 'react';
import { ReactSortable } from 'react-sortablejs';
import { useTheme } from 'styled-components';
import {
  MdDragIndicator,
  MdSearch,
  MdVerticalAlignTop,
  MdVerticalAlignBottom,
} from 'react-icons/md';

import Modal from '../../../../components/Modal';
import {
  FixedOffIcon,
  FixedOnIcon,
  VisibilityOffIcon,
  VisibilityOnIcon,
} from '../../../../icons';
import { ColumnConfig, defaultColumnsConfig } from '../constants/defaultColumnsConfig';
import * as s from './OrdersColumnsConfigStyles';

type Props = {
  visible?: boolean;
  closeModal: () => void;
  columnsConfig: ColumnConfig[];
  setColumnsConfig: (v: ColumnConfig[]) => void;
};

type ColumnConfigSortable = ColumnConfig & {
  id: string;
};

const OrdersColumnsConfig = ({
  closeModal,
  visible = false,
  columnsConfig,
  setColumnsConfig,
}: Props) => {
  const theme = useTheme();
  const [columnsConfigSortabel, setColumnsConfigSortabel] = useState<ColumnConfigSortable[]>(() => {
    return columnsConfig.map((c) => ({ ...c, id: c.key })).filter((c) => c.subItems.length == 0);
  });

  const allVisible = columnsConfigSortabel.every((column) => column.visible);
  const [filterText, setFilterText] = useState<string>('');

  useEffect(() => {
    if (visible) {
      setColumnsConfigSortabel(
        columnsConfig.map((column) => ({ ...column, id: column.key }))
          .filter((column) => column.subItems.length === 0),
      );
      setFilterText('');
    }
  }, [visible, columnsConfig]);

  const restoreDefaultConfig = () => {
    setColumnsConfigSortabel(
      defaultColumnsConfig.map((column) => ({ ...column, id: column.key }))
        .filter((column) => column.subItems.length === 0),
    );
    setFilterText('');
  };

  // Computed stats
  const stats = useMemo(() => {
    const visible = columnsConfigSortabel.filter((c) => c.visible).length;
    const fixed = columnsConfigSortabel.filter((c) => c.fixed).length;
    const total = columnsConfigSortabel.length;
    return { visible, fixed, total };
  }, [columnsConfigSortabel]);

  const handleVisibility = (itemId: string) => {
    const columnsConfigSortableCopy = columnsConfigSortabel.map((item) =>
      item.id === itemId ? { ...item, visible: !item.visible } : item,
    );
    setColumnsConfigSortabel(columnsConfigSortableCopy);
  };

  const handleFixation = (itemId: string) => {
    const columnsConfigSortableCopy = columnsConfigSortabel.map((item) =>
      item.id === itemId ? { ...item, fixed: !item.fixed } : item,
    );
    setColumnsConfigSortabel(columnsConfigSortableCopy);
  };

  const moveToFirstPosition = (itemId: string) => {
    const columnsConfigSortableCopy = [...columnsConfigSortabel];
    const itemIndex = columnsConfigSortableCopy.findIndex((item) => item.id === itemId);
    const [movedItem] = columnsConfigSortableCopy.splice(itemIndex, 1);
    columnsConfigSortableCopy.unshift(movedItem);
    setColumnsConfigSortabel(columnsConfigSortableCopy);
  };

  const moveToLastPosition = (itemId: string) => {
    const columnsConfigSortableCopy = [...columnsConfigSortabel];
    const itemIndex = columnsConfigSortableCopy.findIndex((item) => item.id === itemId);
    const [movedItem] = columnsConfigSortableCopy.splice(itemIndex, 1);
    columnsConfigSortableCopy.push(movedItem);
    setColumnsConfigSortabel(columnsConfigSortableCopy);
  };

  const toggleAllVisibility = () => {
    const columnsConfigSortableCopy = columnsConfigSortabel.map((item) => ({
      ...item,
      visible: !allVisible,
    }));
    setColumnsConfigSortabel(columnsConfigSortableCopy);
  };

  const handleSave = () => {
    setColumnsConfig(columnsConfigSortabel);
    closeModal();
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
  };

  const normalizeSearch = (value: string) => value.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const search = normalizeSearch(filterText);
  const filteredColumnsConfigSortable = columnsConfigSortabel.filter(
    (item) => normalizeSearch(t(item.label)).includes(search)
      || normalizeSearch(item.key).includes(search),
  );

  const reorderFilteredColumns = (reorderedColumns: ColumnConfigSortable[]) => {
    const filteredIds = new Set(filteredColumnsConfigSortable.map((column) => column.id));
    setColumnsConfigSortabel((columns) => {
      let index = 0;
      return columns.map((column) => filteredIds.has(column.id)
        ? reorderedColumns[index++] : column);
    });
  };

  return (
    <Modal
      title={t('tableConfig')}
      showModal={visible}
      closeModal={closeModal}
      cancelButton={closeModal}
      actionNameBtn={t('save')}
      action={() => handleSave()}
    >
      <s.OrderColumnsConfigWrapper>
        <button type="button" className="default-config-button" onClick={restoreDefaultConfig}>
          {t('defaultTableConfig') as string}
        </button>
        {/* Search Bar */}
        <div className="search-bar">
          <MdSearch size="1.25rem" color={theme.colors.textLight} />
          <input
            className="search-input"
            type="text"
            placeholder={t('searchColumns')}
            aria-label={t('searchColumns')}
            value={filterText}
            onChange={handleFilterChange}
          />
          <button
            className={`toggle-all-button ${allVisible ? 'active' : ''}`}
            onClick={toggleAllVisibility}
            title={allVisible ? t('hideAll') : t('showAll')}
          >
            {allVisible ? <VisibilityOnIcon size="1rem" /> : <VisibilityOffIcon size="1rem" />}
            {allVisible ? (t('hideAll') as string) : (t('showAll') as string)}
          </button>
        </div>

        {/* Stats */}
        <div className="stats-bar">
          <span className="stat-badge default">
            {stats.total} {t('total') as string}
          </span>
          <span className="stat-badge success">
            {stats.visible} {t('visible') as string}
          </span>
          <span className="stat-badge primary">
            {stats.fixed} {t('fixed') as string}
          </span>
        </div>

        {/* Columns List */}
        <div className="columns-list">
          {filteredColumnsConfigSortable.length === 0 ? (
            <div className="empty-search">
              <MdSearch />
              <p>{t('noColumnsFound') as string}</p>
            </div>
          ) : (
            <ReactSortable
              list={filteredColumnsConfigSortable}
              setList={reorderFilteredColumns}
              handle=".drag-handle"
            >
              {filteredColumnsConfigSortable.map((column) => (
                <div
                  key={column.id}
                  className={
                    `column-item ${column.visible ? 'visible' : ''} ${column.fixed ? 'fixed' : ''}`
                  }
                >
                  <div className="column-name">
                    <span className="drag-handle">
                      <MdDragIndicator />
                    </span>
                    {t(column.label) as string}
                  </div>

                  <div className="column-actions">
                    <button
                      className={`action-icon-button ${column.fixed ? 'active primary' : ''}`}
                      onClick={() => handleFixation(column.id)}
                      title={column.fixed ? t('unfix') : t('fix')}
                    >
                      {column.fixed ? (
                        <FixedOnIcon size="1.125rem" />
                      ) : (
                        <FixedOffIcon size="1.125rem" />
                      )}
                    </button>

                    <button
                      className={`action-icon-button ${column.visible ? 'active success' : ''}`}
                      onClick={() => handleVisibility(column.id)}
                      title={column.visible ? t('hide') : t('show')}
                    >
                      {column.visible ? (
                        <VisibilityOnIcon size="1.125rem" />
                      ) : (
                        <VisibilityOffIcon size="1.125rem" />
                      )}
                    </button>

                    <div className="move-buttons-group">
                      <button
                        className="action-icon-button"
                        onClick={() => moveToFirstPosition(column.id)}
                        title={t('moveToFirst')}
                      >
                        <MdVerticalAlignTop />
                      </button>
                      <button
                        className="action-icon-button"
                        onClick={() => moveToLastPosition(column.id)}
                        title={t('moveToLast')}
                      >
                        <MdVerticalAlignBottom />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </ReactSortable>
          )}
        </div>
      </s.OrderColumnsConfigWrapper>
    </Modal>
  );
};

export default OrdersColumnsConfig;
